import {Mutex} from 'async-mutex';
import * as Media from '@webex/internal-media-core';
import {LOGGER} from '../Logger/types';
import {
  getTestUtilsWebex,
  getMockRequestTemplate,
  getMobiusDiscoveryResponse,
} from '../common/testUtil';
import {
  CallType,
  RegistrationStatus,
  ServiceIndicator,
  ALLOWED_SERVICES,
  HTTP_METHODS,
  WebexRequestPayload,
} from '../common/types';
/* eslint-disable dot-notation */
import {CALLING_CLIENT_EVENT_KEYS, CallSessionEvent, MOBIUS_EVENT_KEYS} from '../Events/types';
import log from '../Logger';
import {createClient} from './CallingClient';
import {ICallingClient} from './types';
import * as utils from '../common/Utils';
import {getCallManager} from './calling/callManager';
import {
  CALLING_CLIENT_FILE,
  DISCOVERY_URL,
  NETWORK_FLAP_TIMEOUT,
  REGISTRATION_FILE,
  CALLING_USER_AGENT,
  CISCO_DEVICE_URL,
  SPARK_USER_AGENT,
  URL_ENDPOINT,
} from './constants';
import {MOCK_MULTIPLE_SESSIONS_EVENT, MOCK_SESSION_EVENT} from './callRecordFixtures';
import {ILine} from './line/types';
import {
  regionBody,
  regionPayload,
  primaryUrl,
  discoveryPayload,
  registrationPayload,
  mockEUServiceHosts,
  mockIntServiceHosts,
  mockEUIntServiceHosts,
  mockCatalogEU,
  mockCatalogUSInt,
  mockCatalogUS,
  mockCatalogEUInt,
  mockUSServiceHosts,
} from './callingClientFixtures';
import Line from './line';
import {filterMobiusUris} from '../common/Utils';
import {URL} from './registration/registerFixtures';
import {ICall} from './calling/types';
import {ServiceHost} from '../SDKConnector/types';
import {METHOD_START_MESSAGE} from '../common/constants';
import {METRIC_EVENT, CONNECTION_ACTION, METRIC_TYPE} from '../Metrics/types';
import windowsChromiumIceWarmup from './windowsChromiumIceWarmupUtils';

global.crypto = {
  randomUUID: () => '12345678-1234-5678-1234-567812345678',
} as unknown as Crypto;

jest.spyOn(utils, 'uploadLogs').mockResolvedValue(undefined);

describe('CallingClient Tests', () => {
  // Common initializers

  const handleErrorSpy = jest.spyOn(utils, 'handleCallingClientErrors');
  const setLoggerSpy = jest.spyOn(Media, 'setLogger');
  const webex = getTestUtilsWebex();
  webex.internal.services['_hostCatalog'] = mockCatalogUS;
  webex.internal.services.getMobiusClusters = jest.fn().mockReturnValue(mockUSServiceHosts);
  const defaultServiceIndicator = ServiceIndicator.CALLING;
  const callManager = getCallManager(webex, defaultServiceIndicator);

  const infoSpy = jest.spyOn(log, 'info');
  const logSpy = jest.spyOn(log, 'log');
  const warnSpy = jest.spyOn(log, 'warn');

  const originalProcessNextTick = process.nextTick;
  function flushPromises() {
    return new Promise((resolve) => {
      originalProcessNextTick(resolve);
    });
  }

  describe('CallingClient pick Mobius cluster using Service Host Tests', () => {
    afterAll(() => {
      callManager.removeAllListeners();
      webex.internal.services['_serviceUrls']['mobius'] =
        'https://mobius.aintgen-a-1.int.infra.webex.com/api/v1';
      webex.internal.services['_hostCatalog'] = mockCatalogUS;
    });

    it('should use default mobius service host when Service URL is invalid', async () => {
      webex.internal.services._hostCatalog = mockCatalogUS;
      webex.internal.services._serviceUrls.mobius = 'invalid-url';

      const callingClient = await createClient(webex, {logger: {level: LOGGER.INFO}});
      expect(setLoggerSpy).toHaveBeenCalledTimes(1);
      expect(callingClient['mobiusClusters']).toStrictEqual(mockUSServiceHosts);
    });

    it('should set mobiusServiceHost correctly when URL is valid', async () => {
      webex.internal.services._hostCatalog = mockCatalogEU;
      webex.internal.services.getMobiusClusters = jest.fn().mockReturnValue(mockEUServiceHosts);
      webex.internal.services['_serviceUrls']['mobius'] =
        'https://mobius-eu-central-1.prod.infra.webex.com/api/v1';

      const callingClient = await createClient(webex, {logger: {level: LOGGER.INFO}});

      expect(callingClient['mobiusHost']).toBe(
        'https://mobius-eu-central-1.prod.infra.webex.com/api/v1'
      );
      expect(callingClient['mobiusClusters']).toStrictEqual(mockEUServiceHosts);
    });
  });

  describe('ServiceData tests', () => {
    let callingClient: ICallingClient | undefined;

    afterEach(() => {
      callManager.removeAllListeners();
      callingClient = undefined;
    });

    /**
     * No input sdk config to callingClient, it should default to service type
     * calling and empty domain value.
     */
    it('Verify valid calling serviceData with no input sdk config', async () => {
      expect(async () => {
        callingClient = await createClient(webex);
        expect(callingClient).toBeTruthy();
      }).not.toThrow(Error);
    });

    /**
     * Input sdk config to callingClient with serviceData carrying empty string for
     * both indicator and domain in it.
     *
     * It should default to service type calling and empty domain value.
     *
     */
    it('Verify invalid empty service indicator, empty domain', async () => {
      /* eslint-disable @typescript-eslint/no-explicit-any */
      const serviceDataObj: any = {indicator: '', domain: ''};

      expect(async () => {
        callingClient = await createClient(webex, {serviceData: serviceDataObj});
        expect(callingClient).toBeTruthy();
      }).not.toThrow(Error);
    });

    /**
     * Input sdk config to callingClient with serviceData carrying invalid value for indicator
     * and valid domain type string for domain field in it.
     *
     * It should throw error and abort execution as indicator value is invalid.
     *
     * INDICATOR value MUST MATCH with one of the values in ServiceIndicator enum.
     */
    it('Verify invalid service indicator, valid domain', async () => {
      /* eslint-disable @typescript-eslint/no-explicit-any */
      const serviceDataObj: any = {indicator: 'test', domain: 'test.example.com'};

      try {
        callingClient = await createClient(webex, {serviceData: serviceDataObj});
      } catch (e) {
        expect(e.message).toEqual(
          'Invalid service indicator, Allowed values are: calling, contactcenter and guestcalling'
        );
      }
      expect.assertions(1);
    });

    /**
     * Input sdk config to callingClient with serviceData carrying valid value for indicator
     * 'calling', but an invalid domain type string for domain field in it.
     *
     * It should throw error and abort execution as domain value is invalid.
     *
     * DOMAIN field for service type calling is allowed to be empty but if it carries a non-empty
     * string then it must be of valid domain type.
     */
    it('CALLING: verify invalid service domain', async () => {
      const serviceDataObj = {indicator: ServiceIndicator.CALLING, domain: 'test'};

      try {
        callingClient = await createClient(webex, {serviceData: serviceDataObj});
      } catch (e) {
        expect(e.message).toEqual('Invalid service domain.');
      }
      expect.assertions(1);
    });

    /**
     * Input sdk config to callingClient with serviceData carrying valid value for indicator
     * 'calling', and an empty string for domain field in it.
     *
     * Execution should proceed properly and createRegistration should be called with same serviceData.
     *
     * DOMAIN field for service type 'calling' is allowed to be empty.
     */
    it('CALLING: verify valid empty service domain', async () => {
      const serviceDataObj = {indicator: ServiceIndicator.CALLING, domain: ''};

      expect(async () => {
        callingClient = await createClient(webex, {serviceData: serviceDataObj});
        expect(callingClient).toBeTruthy();
      }).not.toThrow(Error);
    });

    /**
     * Input sdk config to callingClient with serviceData carrying valid value for indicator
     * 'contactcenter', but an empty string for domain field in it.
     *
     * It should throw error and abort execution as domain value is invalid.
     *
     * DOMAIN field for service type 'contactcenter' must carry a non-empty valid domain type string.
     */
    it('ContactCenter: verify empty invalid service domain', async () => {
      const serviceDataObj = {indicator: ServiceIndicator.CONTACT_CENTER, domain: ''};

      try {
        callingClient = await createClient(webex, {serviceData: serviceDataObj});
      } catch (e) {
        expect(e.message).toEqual('Invalid service domain.');
      }
      expect.assertions(1);
    });

    /**
     * Input sdk config to callingClient with serviceData carrying valid value for indicator
     * 'contactcenter' , and a valid domain type string for domain field in it.
     *
     * Execution should proceed properly and createRegistration should be called with same serviceData.
     *
     * DOMAIN field for service type 'contactcenter' must carry a non-empty valid domain type string.
     */
    it('ContactCenter: verify valid service domain', async () => {
      const serviceDataObj = {
        indicator: ServiceIndicator.CONTACT_CENTER,
        domain: 'test.example.com',
      };

      expect(async () => {
        callingClient = await createClient(webex, {serviceData: serviceDataObj});
        expect(callingClient).toBeTruthy();
      }).not.toThrow(Error);
    });

    it('Get current log level', async () => {
      callingClient = await createClient(webex, {logger: {level: LOGGER.INFO}});
      expect(callingClient).toBeTruthy();
      expect(callingClient.getLoggingLevel).toBeTruthy();
      expect(callingClient.getLoggingLevel()).toEqual(LOGGER.INFO);
    });
  });

  describe('Mobius Server discovery tests', () => {
    let callingClient;

    afterEach(() => {
      jest.clearAllTimers();
      jest.clearAllMocks();
      callingClient.removeAllListeners();
      callManager.removeAllListeners();
      callingClient = undefined;
      jest.useRealTimers();
    });

    it('verify successful mobius server url discovery', async () => {
      webex.request.mockResolvedValueOnce(regionPayload).mockResolvedValueOnce(discoveryPayload);

      callingClient = await createClient(webex, {logger: {level: LOGGER.INFO}});

      expect(webex.request).toBeCalledTimes(2);
      expect(callingClient.primaryMobiusUris).toEqual([primaryUrl]);
      expect(handleErrorSpy).not.toBeCalled();

      expect(webex.request).nthCalledWith(1, {
        method: 'GET',
        uri: `${DISCOVERY_URL}`,
        addAuthHeader: false,
        headers: {
          [SPARK_USER_AGENT]: null,
        },
      });

      expect(webex.request).nthCalledWith(2, {
        method: 'GET',
        ...getMockRequestTemplate(),
        uri: `${callingClient['mobiusHost']}${URL_ENDPOINT}?regionCode=${regionBody.clientRegion}&countryCode=${regionBody.countryCode}`,
      });
    });

    it('when region discovery succeeds but region based mobius url discovery fails with final error', async () => {
      const failurePayload = {
        statusCode: 401,
      };

      webex.request.mockResolvedValueOnce(regionPayload).mockRejectedValueOnce(failurePayload);

      callingClient = await createClient(webex, {logger: {level: LOGGER.INFO}});

      expect(handleErrorSpy).toBeCalledWith(failurePayload, expect.anything(), {
        file: CALLING_CLIENT_FILE,
        method: 'getMobiusServers',
      });
      expect(webex.request).toBeCalledTimes(2);

      expect(callingClient.primaryMobiusUris).toEqual([
        `${callingClient['mobiusHost']}${URL_ENDPOINT}`,
      ]);

      expect(webex.request).nthCalledWith(1, {
        method: 'GET',
        uri: `${DISCOVERY_URL}`,
        addAuthHeader: false,
        headers: {
          [SPARK_USER_AGENT]: null,
        },
      });

      expect(warnSpy).toBeCalledWith(
        `Couldn't resolve the region and country code. Defaulting to the catalog entries to discover mobius servers`,
        expect.objectContaining({
          file: CALLING_CLIENT_FILE,
          method: 'getMobiusServers',
        })
      );
    });

    it('Verify successful mobius server url discovery after initializing callingClient through a config', async () => {
      infoSpy.mockReset();
      webex.request.mockResolvedValueOnce(discoveryPayload);

      callingClient = await createClient(webex, {
        discovery: {
          region: 'AP-SOUTHEAST',
          country: 'IN',
        },
        logger: {
          level: LOGGER.INFO,
        },
      });

      expect(callingClient.primaryMobiusUris).toEqual([primaryUrl]);

      expect(infoSpy).toHaveBeenCalledWith(METHOD_START_MESSAGE, {
        file: 'CallingClient',
        method: 'getMobiusServers',
      });

      expect(webex.request).toBeCalledOnceWith({
        ...getMockRequestTemplate(),
        uri: `${callingClient['mobiusHost']}${URL_ENDPOINT}?regionCode=${regionBody.clientRegion}&countryCode=${regionBody.countryCode}`,
        method: 'GET',
      });

      expect(handleErrorSpy).not.toHaveBeenCalled();
    });
  });

  describe('Testing each cluster present withing host catalog', () => {
    const mobiusCluster = [
      'mobius-eu-central-1.prod.infra.webex.com',
      'mobius-us-east-1.int.infra.webex.com',
      'mobius-eu-central-1.int.infra.webex.com',
    ];

    const checkCluster = async (
      mockServiceHosts: ServiceHost[],
      mockCatalog: Record<string, ServiceHost[]>
    ) => {
      webex.internal.services._hostCatalog = mockCatalog;
      webex.internal.services.getMobiusClusters = jest.fn().mockReturnValue(mockServiceHosts);
      const callingClient = await createClient(webex, {logger: {level: LOGGER.INFO}});

      expect(callingClient['mobiusClusters']).toStrictEqual(mockServiceHosts);
    };

    it.each(mobiusCluster)('%s', async (clusterName) => {
      switch (clusterName) {
        case 'mobius-eu-central-1.prod.infra.webex.com':
          checkCluster(mockEUServiceHosts, mockCatalogEU);
          break;
        case 'mobius-us-east-1.int.infra.webex.com':
          checkCluster(mockIntServiceHosts, mockCatalogUSInt);
          break;
        case 'mobius-eu-central-1.int.infra.webex.com':
          checkCluster(mockEUIntServiceHosts, mockCatalogEUInt);
          break;
        default:
          break;
      }
    });
  });

  describe('Line creation tests', () => {
    let callingClient;
    let line;

    beforeEach(async () => {
      callingClient = await createClient(webex);
      line = Object.values(callingClient.lineDict)[0];
    });

    afterEach(() => {
      jest.clearAllTimers();
      jest.clearAllMocks();
      callingClient.removeAllListeners();
      callManager.removeAllListeners();
      callingClient = undefined;
      jest.useRealTimers();
    });

    it('verify line dict successfully created', () => {
      expect(callingClient.lineDict).toBeTruthy();
      expect(line).toBeTruthy();
      expect(Object.keys(callingClient.lineDict).length).toEqual(1);
    });

    it('verify getLines response', () => {
      expect(callingClient.getLines).toBeTruthy();
      expect(callingClient.getLines()).toEqual(callingClient.lineDict);
    });
  });

  describe('Connectivity and Mercury event handlers', () => {
    let callingClient: ICallingClient;
    let line;
    let reg;
    let windowEventSpy: jest.SpyInstance;
    let deRegSpy;
    let restoreSpy;
    let restartRegisterSpy;
    let registerSpy;
    let metricSpy;

    beforeEach(async () => {
      jest.useFakeTimers();
      webex.request
        .mockResolvedValueOnce(regionPayload)
        .mockResolvedValueOnce(discoveryPayload)
        .mockResolvedValueOnce(registrationPayload);

      windowEventSpy = jest.spyOn(window, 'addEventListener');
      callingClient = await createClient(webex, {logger: {level: LOGGER.INFO}});
      line = Object.values(callingClient.lineDict)[0] as ILine;
      reg = line.registration;
      await line.register();

      deRegSpy = jest.spyOn(reg, 'deregister');
      restoreSpy = jest.spyOn(reg, 'restorePreviousRegistration');
      restartRegisterSpy = jest.spyOn(reg, 'restartRegistration');
      registerSpy = jest.spyOn(reg, 'attemptRegistrationWithServers');
      metricSpy = jest.spyOn(callingClient['metricManager'], 'submitConnectionMetrics');
    });

    afterEach(() => {
      jest.clearAllTimers();
      jest.clearAllMocks();
      callingClient.removeAllListeners();
      callingClient['callManager'].callCollection = {};
      callManager.removeAllListeners();
      callingClient = undefined;
      jest.useRealTimers();
    });

    it('window offline handler sets networkDown and clears keepalive when network is unreachable', async () => {
      const g = global;
      g.fetch = jest.fn().mockRejectedValueOnce(new Error('network fail'));

      const clearKeepaliveSpy = jest.spyOn(reg, 'clearKeepaliveTimer');

      const offlineCb = windowEventSpy.mock.calls[1][1];
      await offlineCb();

      expect(warnSpy).toHaveBeenCalledWith(
        expect.stringContaining('Network connectivity probe failed'),
        {
          file: CALLING_CLIENT_FILE,
          method: 'pingExternal',
        }
      );
      expect(callingClient['isNetworkDown']).toBe(true);
      expect(clearKeepaliveSpy).toHaveBeenCalled();
    });

    it('window offline handler does not clear keepalive when network is reachable', async () => {
      const g = global;
      g.fetch = jest.fn().mockResolvedValueOnce({});

      const clearKeepaliveSpy = jest.spyOn(reg, 'clearKeepaliveTimer');

      const offlineCb = windowEventSpy.mock.calls[1][1];
      await offlineCb();

      expect(g.fetch).toHaveBeenCalledWith(
        'https://www.google.com/generate_204',
        expect.any(Object)
      );
      expect(callingClient['isNetworkDown']).toBe(false);
      expect(clearKeepaliveSpy).not.toHaveBeenCalled();
    });

    it('window online handler sets networkUpTimestamp and logs', async () => {
      const onlineCb = windowEventSpy.mock.calls[0][1];
      await onlineCb();

      expect(callingClient['networkUpTimestamp']).toEqual(expect.any(String));
      expect(infoSpy).toHaveBeenCalledWith(METHOD_START_MESSAGE, {
        file: CALLING_CLIENT_FILE,
        method: 'handleNetworkOnline',
      });
    });

    it('mercury offline submits metrics and sets timestamp', async () => {
      const mercuryOfflineCb = webex.internal.mercury.on.mock.calls[1][1];
      await mercuryOfflineCb();

      expect(warnSpy).toHaveBeenCalledWith(
        expect.stringContaining('Mercury down, waiting for connection to be up'),
        {
          file: CALLING_CLIENT_FILE,
          method: 'handleMercuryOffline',
        }
      );
      expect(callingClient['mercuryDownTimestamp']).toEqual(expect.any(String));
      expect(metricSpy).toHaveBeenCalledWith(
        METRIC_EVENT.CONNECTION_ERROR,
        CONNECTION_ACTION.MERCURY_DOWN,
        METRIC_TYPE.BEHAVIORAL,
        expect.any(String),
        ''
      );
    });

    it('handles reconnection with no active calls after network flap, once mercury comes back up', async () => {
      const handleConnectionRestoreSpy = jest.spyOn(reg, 'handleConnectionRestoration');

      callingClient['isNetworkDown'] = true;
      callingClient['networkDownTimestamp'] = '2023-01-01T00:00:00.000Z';
      callingClient['networkUpTimestamp'] = '2023-01-01T00:01:00.000Z';

      const mercuryOnlineCb = webex.internal.mercury.on.mock.calls[2][1];
      await mercuryOnlineCb();

      // Run the interval once
      jest.advanceTimersByTime(NETWORK_FLAP_TIMEOUT + 100);

      await flushPromises();

      expect(handleConnectionRestoreSpy).toHaveBeenCalledWith(true);
      expect(logSpy).toHaveBeenCalledWith(
        'Network is up again, re-registering with Webex Calling if needed',
        {
          file: REGISTRATION_FILE,
          method: 'handleConnectionRestoration',
        }
      );
      expect(deRegSpy).toHaveBeenCalledWith();
      expect(restoreSpy).toHaveBeenCalledWith('handleConnectionRestoration');
      expect(restartRegisterSpy).toHaveBeenCalledWith('handleConnectionRestoration');
      expect(registerSpy).toHaveBeenCalledWith('handleConnectionRestoration', [
        reg.getActiveMobiusUrl(),
      ]);
      expect(registerSpy).toHaveBeenCalledWith('handleConnectionRestoration', [primaryUrl]);
      expect(metricSpy).toHaveBeenCalledWith(
        METRIC_EVENT.CONNECTION_ERROR,
        CONNECTION_ACTION.NETWORK_FLAP,
        METRIC_TYPE.BEHAVIORAL,
        '2023-01-01T00:00:00.000Z',
        '2023-01-01T00:01:00.000Z'
      );
    });

    it('no reconnection for idle registration with no active calls after network flap, once mercury comes back up', async () => {
      const handleConnectionRestoreSpy = jest.spyOn(reg, 'handleConnectionRestoration');

      callingClient['isNetworkDown'] = true;
      callingClient['networkDownTimestamp'] = '2023-01-01T00:00:00.000Z';
      callingClient['networkUpTimestamp'] = '2023-01-01T00:01:00.000Z';
      reg.setStatus(RegistrationStatus.IDLE);

      const mercuryOnlineCb = webex.internal.mercury.on.mock.calls[2][1];
      await mercuryOnlineCb();

      // Run the interval once
      jest.advanceTimersByTime(NETWORK_FLAP_TIMEOUT + 100);

      await flushPromises();

      expect(handleConnectionRestoreSpy).not.toHaveBeenCalledWith(true);
      expect(logSpy).not.toHaveBeenCalledWith(
        'Network is up again, re-registering with Webex Calling if needed',
        {
          file: REGISTRATION_FILE,
          method: 'handleConnectionRestoration',
        }
      );
      expect(deRegSpy).not.toHaveBeenCalledWith();
      expect(restoreSpy).not.toHaveBeenCalledWith('handleConnectionRestoration');
      expect(restartRegisterSpy).not.toHaveBeenCalledWith('handleConnectionRestoration');
      expect(registerSpy).not.toHaveBeenCalledWith('handleConnectionRestoration', [
        reg.getActiveMobiusUrl(),
      ]);
      expect(registerSpy).not.toHaveBeenCalledWith('handleConnectionRestoration', [primaryUrl]);
      expect(metricSpy).toHaveBeenCalledWith(
        METRIC_EVENT.CONNECTION_ERROR,
        CONNECTION_ACTION.NETWORK_FLAP,
        METRIC_TYPE.BEHAVIORAL,
        '2023-01-01T00:00:00.000Z',
        '2023-01-01T00:01:00.000Z'
      );
    });

    it('handle calls status check once mercury comes back up after a network flap', async () => {
      callingClient['isNetworkDown'] = true;
      const call = callingClient['callManager'].createCall();
      const callKeepaliveSpy = jest
        .spyOn(call, 'postStatus')
        .mockRejectedValueOnce(new Error('keepalive fail'));
      const deleteSpy = jest.spyOn(call, 'delete');
      expect(Object.keys(callingClient['callManager'].getActiveCalls()).length).toBe(1);

      const mercuryOnlineCb = webex.internal.mercury.on.mock.calls[2][1];
      await mercuryOnlineCb();

      expect(callKeepaliveSpy).toHaveBeenCalled();

      await flushPromises();

      expect(warnSpy).toHaveBeenCalledWith(expect.stringContaining('Call Keepalive failed'), {
        file: CALLING_CLIENT_FILE,
        method: 'checkCallStatus',
      });
      expect(call['callStateMachine'].state.value).toBe('S_CALL_CLEARED');
      expect(deleteSpy).toHaveBeenCalled();

      expect(Object.keys(reg.callManager.getActiveCalls()).length).toBe(0);
      expect(metricSpy).toHaveBeenCalledWith(
        METRIC_EVENT.CONNECTION_ERROR,
        CONNECTION_ACTION.NETWORK_FLAP,
        METRIC_TYPE.BEHAVIORAL,
        expect.any(String),
        expect.any(String)
      );
    });

    it('handle calls status check once mercury comes back up with stable network', async () => {
      const call = callingClient['callManager'].createCall();
      const callKeepaliveSpy = jest
        .spyOn(call, 'postStatus')
        .mockRejectedValueOnce(new Error('keepalive fail'));
      const deleteSpy = jest.spyOn(call, 'delete');
      expect(Object.keys(callingClient['callManager'].getActiveCalls()).length).toBe(1);

      const mercuryOnlineCb = webex.internal.mercury.on.mock.calls[2][1];
      await mercuryOnlineCb();

      expect(callKeepaliveSpy).toHaveBeenCalled();

      await flushPromises();

      expect(warnSpy).toHaveBeenCalledWith(expect.stringContaining('Call Keepalive failed'), {
        file: CALLING_CLIENT_FILE,
        method: 'checkCallStatus',
      });
      expect(call['callStateMachine'].state.value).toBe('S_CALL_CLEARED');
      expect(deleteSpy).toHaveBeenCalled();

      expect(Object.keys(reg.callManager.getActiveCalls()).length).toBe(0);
      expect(metricSpy).toHaveBeenCalledWith(
        METRIC_EVENT.CONNECTION_ERROR,
        CONNECTION_ACTION.MERCURY_UP,
        METRIC_TYPE.BEHAVIORAL,
        expect.any(String),
        expect.any(String)
      );
    });
  });

  // Calling related test cases
  describe('Calling tests', () => {
    const mutex = new Mutex();
    const userId = webex.internal.device.userId;
    const clientDeviceUri = webex.internal.device.url;
    const mobiusUris = filterMobiusUris(getMobiusDiscoveryResponse(), URL);
    const primaryMobiusUris = jest.fn(() => mobiusUris.primary);
    const backupMobiusUris = jest.fn(() => mobiusUris.backup);

    let callingClient;
    let line: Line;

    beforeAll(async () => {
      callingClient = await createClient(webex);
      line = new Line(
        userId,
        clientDeviceUri,
        mutex,
        primaryMobiusUris(),
        backupMobiusUris(),
        LOGGER.INFO
      );
      const calls = Object.values(callManager.getActiveCalls());
      calls.forEach((call) => {
        call.end();
      });
    });

    afterAll(() => {
      callingClient.removeAllListeners();
      callManager.removeAllListeners();
    });

    it('verify calling client object', () => {
      expect(callingClient.getSDKConnector().getWebex().internal.device.userId).toBe(
        '8a67806f-fc4d-446b-a131-31e71ea5b0e9'
      );
    });

    it('returns undefined when there is no connected call', () => {
      line.register();
      line.makeCall({address: '123456', type: CallType.URI});
      expect(callingClient.getConnectedCall()).toEqual(undefined);
    });

    it('returns the connected call', () => {
      line.register();
      const mockCall = line.makeCall({address: '1234', type: CallType.URI});
      const mockCall2 = line.makeCall({address: '5678', type: CallType.URI});
      // Connected call
      mockCall['connected'] = true;
      mockCall['earlyMedia'] = false;
      mockCall['callStateMachine'].state.value = 'S_CALL_ESTABLISHED';

      // Held call
      mockCall2['connected'] = true;
      mockCall2['held'] = true;
      mockCall2['earlyMedia'] = false;
      mockCall2['callStateMachine'].state.value = 'S_CALL_HOLD';

      const mockActiveCalls: Record<string, ICall> = {
        mockCorrelationId: mockCall as ICall,
        mockCorrelationId2: mockCall2 as ICall,
      };

      jest.spyOn(callManager, 'getActiveCalls').mockReturnValue(mockActiveCalls);
      expect(callingClient.getConnectedCall()).toEqual(mockCall);
    });
    it('returns all active calls', () => {
      callingClient.lineDict = {
        mockDeviceId: {lineId: 'mockLineId'} as ILine,
        mockDeviceId2: {lineId: 'mockLineId2'} as ILine,
      };

      const mockCall = line.makeCall({address: '1234', type: CallType.URI});
      const mockCall2 = line.makeCall({address: '5678', type: CallType.URI});
      const mockCall3 = line.makeCall({address: '9101', type: CallType.URI});

      mockCall.lineId = 'mockLineId';
      mockCall2.lineId = 'mockLineId2';
      mockCall3.lineId = 'mockLineId2';

      const mockActiveCalls: Record<string, ICall> = {
        mockCorrelationId: mockCall as ICall,
        mockCorrelationId2: mockCall2 as ICall,
        mockCorrelationId3: mockCall3 as ICall,
      };

      jest.spyOn(callManager, 'getActiveCalls').mockReturnValue(mockActiveCalls);
      expect(callingClient.getActiveCalls()).toEqual({
        mockLineId: [mockCall],
        mockLineId2: [mockCall2, mockCall3],
      });
    });
  });

  describe('Call Session Event test', () => {
    const mockOn = webex.internal.mercury.on;
    let callingClient: ICallingClient;

    beforeEach(async () => {
      callingClient = await createClient(webex, {logger: {level: LOGGER.INFO}});
    });

    afterEach(() => {
      callingClient.removeAllListeners();
      callManager.removeAllListeners();
    });

    it('verify the recent user session event ', (done) => {
      expect.assertions(2);
      callingClient.on(CALLING_CLIENT_EVENT_KEYS.USER_SESSION_INFO, (event: CallSessionEvent) => {
        expect(event.data).toEqual(MOCK_SESSION_EVENT.data);
        done();
      });

      expect(mockOn.mock.calls[0][0]).toEqual(MOBIUS_EVENT_KEYS.CALL_SESSION_EVENT_INCLUSIVE);
      const callSessionCallback = mockOn.mock.calls[0][1];

      callSessionCallback(MOCK_SESSION_EVENT);
    });

    it('drop the recent user session if there is no webex calling type', (done) => {
      expect.assertions(2);
      callingClient.on(CALLING_CLIENT_EVENT_KEYS.USER_SESSION_INFO, (event: CallSessionEvent) => {
        expect(event.data.userSessions.userSessions.length).toEqual(1);
        done();
      });

      expect(mockOn.mock.calls[0][0]).toEqual(MOBIUS_EVENT_KEYS.CALL_SESSION_EVENT_INCLUSIVE);
      const callSessionCallback = mockOn.mock.calls[0][1];

      callSessionCallback(MOCK_MULTIPLE_SESSIONS_EVENT);
    });
  });

  describe('getDevices', () => {
    let callingClient: ICallingClient;
    const primaryMobius = 'https://mobius.primary/api/v1/calling/web/';
    const backupMobius = 'https://mobius.backup/api/v1/calling/web/';

    beforeEach(async () => {
      callingClient = await createClient(webex, {logger: {level: LOGGER.INFO}});
      callingClient.primaryMobiusUris = [primaryMobius];
      callingClient.backupMobiusUris = [backupMobius];
      (webex.request as jest.Mock).mockClear();
    });

    afterEach(() => {
      callingClient.removeAllListeners();
    });

    it('fetches devices for the provided userId', async () => {
      const devices = [
        {
          deviceId: 'device-1',
          uri: 'https://mobius.test/api/v1/calling/web/devices/device-1',
          status: 'ACTIVE',
          lastSeen: '2024-01-01T00:00:00Z',
          addresses: [],
          clientDeviceUri: 'client-device-uri',
        },
      ];

      const responsePayload = <WebexRequestPayload>(<unknown>{
        statusCode: 200,
        body: {userId: 'user-123', devices},
      });

      (webex.request as jest.Mock).mockResolvedValue(responsePayload);

      const response = await callingClient.getDevices('user-123');

      expect(webex.request).toHaveBeenCalledWith({
        uri: 'https://mobius.primary/api/v1/calling/web/devices?userid=user-123',
        method: HTTP_METHODS.GET,
        service: ALLOWED_SERVICES.MOBIUS,
        headers: {
          [CISCO_DEVICE_URL]: webex.internal.device.url,
          [SPARK_USER_AGENT]: CALLING_USER_AGENT,
        },
      });

      expect(response).toEqual(devices);
    });

    it('falls back to backup Mobius when primary fails', async () => {
      const devices = [
        {
          deviceId: 'device-2',
          uri: 'https://mobius.backup/api/v1/calling/web/devices/device-2',
          status: 'ACTIVE',
          lastSeen: '2024-01-01T00:00:00Z',
          addresses: [],
          clientDeviceUri: 'client-device-uri',
        },
      ];

      const failurePayload = {
        statusCode: 404,
      };

      const responsePayload = <WebexRequestPayload>(<unknown>{
        statusCode: 200,
        body: {userId: 'user-123', devices},
      });

      (webex.request as jest.Mock)
        .mockRejectedValueOnce(failurePayload)
        .mockResolvedValueOnce(responsePayload);

      const response = await callingClient.getDevices('user-123');

      const requestCalls = (webex.request as jest.Mock).mock.calls;
      expect(requestCalls[0][0]).toEqual({
        uri: 'https://mobius.primary/api/v1/calling/web/devices?userid=user-123',
        method: HTTP_METHODS.GET,
        service: ALLOWED_SERVICES.MOBIUS,
        headers: {
          [CISCO_DEVICE_URL]: webex.internal.device.url,
          [SPARK_USER_AGENT]: CALLING_USER_AGENT,
        },
      });

      expect(requestCalls[1][0]).toEqual({
        uri: 'https://mobius.backup/api/v1/calling/web/devices?userid=user-123',
        method: HTTP_METHODS.GET,
        service: ALLOWED_SERVICES.MOBIUS,
        headers: {
          [CISCO_DEVICE_URL]: webex.internal.device.url,
          [SPARK_USER_AGENT]: CALLING_USER_AGENT,
        },
      });

      expect(response).toEqual(devices);
    });
  });

  describe('windowsChromiumIceWarmup', () => {
    let origRTCPeerConnection: any;

    beforeAll(() => {
      origRTCPeerConnection = global.RTCPeerConnection;
      // Minimal mock for RTCPeerConnection
      global.RTCPeerConnection = class {
        static instances: any[] = [];
        iceGatheringState = 'complete';
        localDescription = null;
        remoteDescription = null;
        onicecandidate: ((e: any) => void) | null = null;
        constructor() {
          (this.constructor as any).instances.push(this);
        }

        createDataChannel() {
          return {};
        }

        addTransceiver() {
          return {};
        }

        createOffer() {
          return Promise.resolve({sdp: 'offer', type: 'offer'});
        }

        setLocalDescription(desc: any) {
          this.localDescription = desc;

          return Promise.resolve();
        }

        setRemoteDescription(desc: any) {
          this.remoteDescription = desc;

          return Promise.resolve();
        }

        createAnswer() {
          return Promise.resolve({sdp: 'answer', type: 'answer'});
        }

        addIceCandidate() {
          return Promise.resolve();
        }

        close() {}
        addEventListener() {}
        removeEventListener() {}
      };
    });

    afterAll(() => {
      global.RTCPeerConnection = origRTCPeerConnection;
    });

    it('should complete without throwing', async () => {
      await expect(windowsChromiumIceWarmup({})).resolves.not.toThrow();
    });
  });
});
